import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ActionDialog from '../ActionDialog';
import type { DialogContent } from '../../utils/errorPresentation';

export type ConfirmOptions = Omit<DialogContent, 'tone'> & {
  /** `error` for something destructive, which also turns the confirm button red. */
  tone?: 'confirm' | 'error';
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Arbitrary content shown between the summary and `items` — e.g. the
   * quotation line-pick confirm's before -> after table
   * (`features/quotations/LinePickPreview.tsx`). See `ActionDialogProps.body`
   * for why this rides beside `DialogContent` rather than widening it.
   */
  body?: React.ReactNode;
  /**
   * Called when the question is closed by Escape, the × or the backdrop rather than either
   * button, or is replaced by a newer question before anyone answered it, before the promise
   * settles `false`. For a question whose Cancel
   * button is itself an answer (D77 "Yes, the AI misread it"), so a dismissal
   * is not recorded as one.
   */
  onDismiss?: () => void;
};

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

/**
 * `if (!(await confirm({ title: 'Delete this client?', tone: 'error', confirmLabel: 'Delete' }))) return;`
 *
 * The in-app replacement for `window.confirm`, one line for one line; there were 37 of those. It
 * renders ActionDialog, so a confirmation keeps that dialog's rules: Escape cancels and never
 * confirms, it sits above a full-screen Modal, and it has no backdrop blur.
 */
export const useConfirm = (): Confirm => {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used within a ConfirmProvider');
  return confirm;
};

type Pending = { resolve: (confirmed: boolean) => void; onDismiss?: () => void };

/** Settle the open question, if any. `dismissed`: nobody pressed either button. */
function closePending(ref: React.MutableRefObject<Pending | null>, confirmed: boolean, dismissed: boolean) {
  const pending = ref.current;
  ref.current = null;
  if (dismissed) pending?.onDismiss?.();
  pending?.resolve(confirmed);
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [request, setRequest] = useState<ConfirmOptions | null>(null);
  const pendingRef = useRef<Pending | null>(null);

  const confirm = useCallback<Confirm>((options) => {
    // A new question while one is still open settles the old one "no" instead of leaving it
    // hanging, as a dismissal: nobody answered it (D77, where Cancel is itself an answer).
    closePending(pendingRef, false, true);
    setRequest(options);
    return new Promise<boolean>((resolve) => {
      pendingRef.current = { resolve, onDismiss: options.onDismiss };
    });
  }, []);

  const settle = useCallback((confirmed: boolean, dismissed = false) => {
    closePending(pendingRef, confirmed, dismissed);
    setRequest(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ActionDialog
        isOpen={request !== null}
        tone={request?.tone ?? 'confirm'}
        title={request?.title ?? ''}
        summary={request?.summary}
        itemsHeading={request?.itemsHeading}
        items={request?.items}
        hint={request?.hint}
        details={request?.details}
        body={request?.body}
        confirmLabel={request?.confirmLabel}
        cancelLabel={request?.cancelLabel}
        onClose={() => settle(false, true)}
        onCancel={() => settle(false)}
        onConfirm={() => settle(true)}
      />
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
